import { Injectable, OnDestroy } from '@angular/core';

import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, map, mergeMap, mergeMapTo, takeUntil } from 'rxjs/operators';

import { AuthService } from '@auth/auth.service';
import { selectAuthData } from '@auth/store/auth.selectors';
import { DataService } from '@data/data.service';

import { environment } from '@environment';

import * as dataActions from './data.actions';

@Injectable()
export class DataEffects implements OnDestroy {
  destroy$ = new Subject<void>();

  private customer$ = this.store.select(selectAuthData).pipe(map((data) => (data ? data.custid : '')));

  constructor(private actions$: Actions, private dataService: DataService, private store: Store, private authService: AuthService) {
    this.customer$.pipe(takeUntil(this.destroy$)).subscribe((_value) => {
      return [new dataActions.ApiPreloadCustomerLists()];
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  apiPreloadLists = createEffect(() => {
    return this.actions$.pipe(
      ofType(dataActions.API_PRELOAD_LISTS),
      mergeMapTo(environment.preloadLists.map((list) => new dataActions.ApiRequestList(list)))
    );
  });

  requestList = createEffect(() => {
    return this.actions$.pipe(
      ofType<dataActions.ApiRequestList>(dataActions.API_REQUEST_LIST),
      mergeMap(({ list }) => this.dataService.getList(list)),
      map((listEntry) => {
        return new dataActions.ApiStoreList(listEntry);
      })
    );
  });

  apiPreloadCustLists = createEffect(() => {
    return this.actions$.pipe(
      ofType(dataActions.API_PRELOAD_CUST_LISTS),
      mergeMapTo(environment.customerPreloadLists.map((list) => new dataActions.ApiRequestCustomerList(list)))
    );
  });

  requestCustList = createEffect(
    () => {
      return this.actions$.pipe(
        ofType<dataActions.ApiRequestCustomerList>(dataActions.API_REQUEST_CUST_LIST),
        concatLatestFrom(() => [this.customer$]),
        filter(([_action, custid]) => custid !== ''),
        mergeMap(([{ list }, custid]) => this.dataService.getCustomerList(custid, list)),
        map((listEntry) => {
          return this.store.dispatch(new dataActions.ApiStoreCustomerList(listEntry));
        })
      );
    },
    { dispatch: false }
  );

  storeErrorReport = createEffect(
    () => {
      return this.actions$.pipe(
        ofType<dataActions.ApiStoreErrorReport>(dataActions.API_STORE_ERROR_REPORT),
        concatLatestFrom(() => [this.authService.authData$, this.dataService.selectedRow$]),
        mergeMap(([{ payload }, authData, selectedRow]) => this.dataService.submitErrorReport(payload, authData, selectedRow))
      );
    },
    { dispatch: false }
  );
}
